import { useMemo, useState } from 'react';
import {
  Table,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Button,
  Spin,
  Badge,
  Tabs,
  Modal,
  Timeline,
  Popconfirm,
  message,
  Alert,
} from 'antd';
import { SearchOutlined, ReloadOutlined, WarningOutlined, SwapRightOutlined, CheckCircleOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getGenericMappings,
  createGenericMapping,
  updateGenericMapping,
  deleteGenericMapping,
  getGenericProjectIdMappings,
  createGenericProjectIdMapping,
  getProjectTypes,
  updateProjectAttributeChangeLogActionTaken,
} from '../../api/project_type';
import { getGHGMethodologies } from '../../api/ghg';

const { Title, Text } = Typography;

type StatusType = 'mapped' | 'needs-mapping';

function getStatusFromCalyxType(calyxType: string | null | undefined): StatusType {
  return calyxType ? 'mapped' : 'needs-mapping';
}

function renderStatusTag(status: StatusType) {
  if (status === 'mapped') return <Tag color="green">Mapped</Tag>;
  return <Tag color="red">Needs Mapping</Tag>;
}

// ─── Registry Type Level Tab ───────────────────────────────────────────

async function fetchGenericMappingsData() {
  const data = await getGenericMappings();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows as any[];
}

function RegistryTypeLevelTab({
  calyxOptions,
}: {
  calyxOptions: { value: string; label: string }[];
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createRegistryMethodology, setCreateRegistryMethodology] = useState('');
  const [createCalyxProjectType, setCreateCalyxProjectType] = useState<string | null>(null);

  const {
    data: rows = [],
    isLoading: mappingsLoading,
    isFetching: mappingsFetching,
  } = useQuery({
    queryKey: ['genericProjectTypeMappings'],
    queryFn: fetchGenericMappingsData,
  });

  const { mutate: updateMapping, isLoading: isUpdating } = useMutation(
    ({ record, calyxProjectType }: { record: any; calyxProjectType: string | null }) =>
      updateGenericMapping({
        registry_methodology: record.registry_methodology_platform?.display_name,
        calyx_project_type: calyxProjectType,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['genericProjectTypeMappings']);
        message.success('Mapping updated');
      },
      onError: () => {
        message.error('Failed to update mapping. Please try again.');
      },
    },
  );

  const { mutate: deleteMapping, isLoading: isDeleting } = useMutation(
    (id: string) => deleteGenericMapping(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['genericProjectTypeMappings']);
        message.success('Mapping deleted');
      },
      onError: () => {
        message.error('Failed to delete mapping. Please try again.');
      },
    },
  );

  const { mutate: createMapping, isLoading: isCreating } = useMutation(
    () =>
      createGenericMapping({
        registry_methodology: createRegistryMethodology.trim(),
        ...(createCalyxProjectType ? { calyx_project_type: createCalyxProjectType } : {}),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['genericProjectTypeMappings']);
        message.success('Mapping created');
        setCreateModalOpen(false);
        setCreateRegistryMethodology('');
        setCreateCalyxProjectType(null);
      },
      onError: (err: any) => {
        message.error(err?.message || 'Failed to create mapping. Please try again.');
      },
    },
  );

  const loading = mappingsLoading || mappingsFetching || isUpdating || isDeleting;

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rows as any[]).filter((row) => {
      const projectTypeName = row.calyx_project_type?.display_name ?? null;
      const status = getStatusFromCalyxType(projectTypeName);
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (!text) return true;
      const haystack = [
        row.harmonized_methodology_platform?.display_name || '',
        row.registry_methodology_platform?.display_name || '',
        projectTypeName || '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(text);
    });
  }, [rows, search, statusFilter]);

  const columns: any[] = [
    {
      title: 'Methodology (Berkeley + Registry)',
      key: 'registry_methodology_platform',
      sorter: (a: any, b: any) =>
        (a.registry_methodology_platform?.display_name || '').localeCompare(
          b.registry_methodology_platform?.display_name || '',
        ),
      render: (_: any, record: any) => record.registry_methodology_platform?.display_name || '—',
    },
    {
      title: 'Harmonized Methodology',
      key: 'harmonized_methodology_platform',
      sorter: (a: any, b: any) =>
        (a.harmonized_methodology_platform?.display_name || '').localeCompare(
          b.harmonized_methodology_platform?.display_name || '',
        ),
      render: (_: any, record: any) => record.harmonized_methodology_platform?.display_name || '—',
    },
    {
      title: 'Calyx Project Type',
      key: 'calyx_project_type',
      width: 300,
      sorter: (a: any, b: any) =>
        (a.calyx_project_type?.display_name || '').localeCompare(b.calyx_project_type?.display_name || ''),
      render: (_: any, record: any) => {
        const value = record.calyx_project_type?.display_name ?? null;
        const options =
          value && !calyxOptions.some((o: any) => o.value === value)
            ? [{ value, label: value }, ...calyxOptions]
            : calyxOptions;
        return (
          <Select
            allowClear
            placeholder="Select Calyx Project Type"
            value={value || undefined}
            options={options}
            onChange={(val) => updateMapping({ record, calyxProjectType: val ?? null })}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a: any, b: any) =>
        getStatusFromCalyxType(a.calyx_project_type?.display_name ?? null).localeCompare(
          getStatusFromCalyxType(b.calyx_project_type?.display_name ?? null),
        ),
      render: (_: any, record: any) => {
        const status = getStatusFromCalyxType(record.calyx_project_type?.display_name ?? null);
        return renderStatusTag(status);
      },
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Delete this mapping?"
          okText="Delete"
          okType="danger"
          onConfirm={() => deleteMapping(record.id)}
        >
          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const needsMappingCount = (rows as any[]).filter(
    (r) => getStatusFromCalyxType(r.calyx_project_type?.display_name ?? null) === 'needs-mapping',
  ).length;

  return (
    <Spin spinning={loading} tip="Loading generic mappings…">
      <div
        style={{
          margin: '16px 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search Methodology, Calyx Project Type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <Space>
          <Text type="secondary">Show:</Text>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 200 }}
            options={[
              { value: 'all', label: 'All types' },
              { value: 'needs-mapping', label: 'Needs mapping' },
              { value: 'mapped', label: 'Mapped' },
            ]}
          />
          <Badge
            count={needsMappingCount}
            size="small"
            style={{ backgroundColor: '#fa541c' }}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => queryClient.invalidateQueries(['genericProjectTypeMappings'])}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalOpen(true)}
          >
            Add Mapping
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="id"
        size="middle"
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
      />
      <Modal
        title="Add Generic Mapping"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          setCreateRegistryMethodology('');
          setCreateCalyxProjectType(null);
        }}
        onOk={() => createMapping()}
        okText="Create"
        confirmLoading={isCreating}
        okButtonProps={{ disabled: !createRegistryMethodology.trim() }}
      >
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <div>
            <Text strong>Registry Methodology</Text>
            <Text type="danger"> *</Text>
            <Input
              style={{ marginTop: 4 }}
              placeholder="Enter registry methodology display name"
              value={createRegistryMethodology}
              onChange={(e) => setCreateRegistryMethodology(e.target.value)}
            />
          </div>
          <div>
            <Text strong>Calyx Project Type</Text>
            <Text type="secondary"> (optional)</Text>
            <Select
              allowClear
              style={{ width: '100%', marginTop: 4 }}
              placeholder="Select Calyx Project Type"
              value={createCalyxProjectType || undefined}
              options={calyxOptions}
              onChange={(val) => setCreateCalyxProjectType(val ?? null)}
              showSearch
              optionFilterProp="label"
            />
          </div>
        </Space>
      </Modal>
    </Spin>
  );
}

// ─── Project ID Level Tab ──────────────────────────────────────────────

async function fetchGenericProjectIdMappingsData() {
  const data = await getGenericProjectIdMappings();
  const rows = Array.isArray(data) ? data : data.response || [];
  return rows.map((row: any, index: number) => ({
    ...row,
    id: row.id ?? `${row.registry || 'REG'}-${row.project_id || 'PID'}-${index}`,
  }));
}

async function updateGenericProjectIdCalyxProjectType(
  record: any,
  calyxType: string | null,
  calyxMethodology: string | null,
) {
  const payload = {
    project_id: record.project_id,
    ghg_standard_code: record.registry,
    project_id_mapped_project_type: calyxType,
    project_id_mapped_methodology: calyxMethodology,
  };
  await createGenericProjectIdMapping(payload);
  return {
    ...record,
    project_id_mapped_project_type: calyxType,
    project_id_mapped_methodology: calyxMethodology,
  };
}

function ChangeLogsModal({
  open,
  changeLogs,
  onClose,
  onActionTakenAll,
  actionLoading,
}: {
  open: boolean;
  changeLogs: any[];
  onClose: () => void;
  onActionTakenAll: () => void;
  actionLoading: boolean;
}) {
  return (
    <Modal
      title={
        <Space>
          <WarningOutlined style={{ color: '#fa8c16' }} />
          <span>Registry Changes Detected</span>
          <Tag color="orange">{changeLogs.length}</Tag>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {changeLogs.length} change{changeLogs.length !== 1 ? 's' : ''} detected
          </Text>
          <Space>
            <Button onClick={onClose}>Close</Button>
            {changeLogs.length > 0 && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={actionLoading}
                onClick={onActionTakenAll}
              >
                Resolve All
              </Button>
            )}
          </Space>
        </div>
      }
      width={560}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 13 }}>
       Please resolve all items only after updating the mapping. If not completed, you can click Close.
      </Text>
      <Timeline
        items={changeLogs.map((log: any) => ({
          key: log.id,
          color: log.field_name === 'project_type' ? 'blue' : 'green',
          children: (
            <div
              style={{
                padding: '8px 12px',
                background: '#fafafa',
                borderRadius: 6,
                border: '1px solid #f0f0f0',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <Tag color={log.field_name === 'project_type' ? 'blue' : 'green'}>
                  {log.field_name === 'project_type' ? 'Project Type' : 'Methodology'}
                </Tag>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {new Date(log.detected_at).toLocaleString()}
                </Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text delete type="secondary" style={{ flex: 1 }}>
                  {log.old_value}
                </Text>
                <SwapRightOutlined style={{ color: '#8c8c8c', flexShrink: 0 }} />
                <Text strong style={{ flex: 1 }}>
                  {log.new_value}
                </Text>
              </div>
            </div>
          ),
        }))}
      />
    </Modal>
  );
}

function ProjectIdLevelTab({
  calyxOptions,
  calyxMethodologyOptions,
}: {
  calyxOptions: { value: string; label: string }[];
  calyxMethodologyOptions: { value: string; label: string }[];
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType>('all');
  const [changeLogModalRecord, setChangeLogModalRecord] = useState<any>(null);

  const {
    data: rows = [],
    isLoading: mappingsLoading,
    isFetching: mappingsFetching,
  } = useQuery({
    queryKey: ['genericProjectIdMappings'],
    queryFn: fetchGenericProjectIdMappingsData,
  });

  const { mutate: markAllActionTaken, isLoading: actionLoading } = useMutation(
    (ids: string[]) =>
      Promise.all(ids.map((id) => updateProjectAttributeChangeLogActionTaken(id, true))),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['genericProjectIdMappings']);
        message.success('All change logs resolved');
        setChangeLogModalRecord(null);
      },
      onError: () => {
        message.error('Failed to resolve change logs. Please try again.');
      },
    },
  );

  const { mutate: updateGenericProjectIdMapping, isLoading: isUpdating } = useMutation(
    ({
      record,
      calyxType,
      calyxMethodology,
    }: {
      record: any;
      calyxType: string | null;
      calyxMethodology: string | null;
    }) => updateGenericProjectIdCalyxProjectType(record, calyxType, calyxMethodology),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['genericProjectIdMappings']);
        message.success('Generic project ID mapping updated');
      },
      onError: () => {
        message.error('Failed to update generic project ID mapping. Please try again.');
      },
    },
  );

  const loading = mappingsLoading || mappingsFetching || isUpdating;

  function getProjectFinalCalyxType(row: any) {
    return row.project_id_mapped_project_type || null;
  }

  const filteredRows = useMemo(() => {
    const text = search.toLowerCase().trim();
    return (rows as any[]).filter((row) => {
      const finalType = getProjectFinalCalyxType(row);
      const status = getStatusFromCalyxType(finalType);
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (!text) return true;
      const haystack = [
        row.registry,
        row.project_id,
        row.project_id_mapped_project_type || '',
        row.project_id_mapped_methodology || '',
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(text);
    });
  }, [rows, search, statusFilter]);

  const handleCalyxTypeChange = (record: any, value: string | null) => {
    updateGenericProjectIdMapping({
      record,
      calyxType: value ?? null,
      calyxMethodology: record.project_id_mapped_methodology ?? null,
    });
  };

  const handleCalyxMethodologyChange = (record: any, value: string | null) => {
    updateGenericProjectIdMapping({
      record,
      calyxType: record.project_id_mapped_project_type ?? null,
      calyxMethodology: value ?? null,
    });
  };

  const columns: any[] = [
    {
      title: 'Project ID',
      dataIndex: 'project_id',
      key: 'project_id',
      width: 130,
      sorter: (a: any, b: any) => (a.project_id || '').localeCompare(b.project_id || ''),
    },
    {
      title: 'Registry',
      dataIndex: 'registry',
      key: 'registry',
      width: 90,
      sorter: (a: any, b: any) => (a.registry || '').localeCompare(b.registry || ''),
    },
    {
      title: '',
      key: 'change_logs',
      width: 50,
      render: (_: any, record: any) => {
        const logs = record.change_logs;
        if (!logs || logs.length === 0) return null;
        return (
          <Button
            size="small"
            type="text"
            icon={<WarningOutlined />}
            style={{ color: '#fa8c16' }}
            onClick={() => setChangeLogModalRecord(record)}
          />
        );
      },
    },
    {
      title: 'Calyx Project Type',
      key: 'calyx_project_type',
      width: 260,
      sorter: (a: any, b: any) =>
        (a.project_id_mapped_project_type || '').localeCompare(b.project_id_mapped_project_type || ''),
      render: (_: any, record: any) => (
        <Select
          allowClear
          placeholder="Select Calyx Project Type"
          value={record.project_id_mapped_project_type || undefined}
          options={calyxOptions}
          onChange={(val) => handleCalyxTypeChange(record, val ?? null)}
          style={{ width: '100%' }}
          showSearch
          optionFilterProp="label"
        />
      ),
    },
    {
      title: 'Calyx Methodology',
      dataIndex: 'project_id_mapped_methodology',
      key: 'project_id_mapped_methodology',
      width: 260,
      sorter: (a: any, b: any) =>
        (a.project_id_mapped_methodology || '').localeCompare(b.project_id_mapped_methodology || ''),
      render: (value: string | null, record: any) => {
        const options =
          value && !calyxMethodologyOptions.some((o) => o.value === value)
            ? [{ value, label: value }, ...calyxMethodologyOptions]
            : calyxMethodologyOptions;
        return (
          <Select
            allowClear
            placeholder="Select Calyx Methodology"
            value={value || undefined}
            options={options}
            onChange={(val) => handleCalyxMethodologyChange(record, val ?? null)}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      sorter: (a: any, b: any) =>
        getStatusFromCalyxType(getProjectFinalCalyxType(a)).localeCompare(
          getStatusFromCalyxType(getProjectFinalCalyxType(b)),
        ),
      render: (_: any, record: any) => {
        const finalType = getProjectFinalCalyxType(record);
        const status = getStatusFromCalyxType(finalType);
        return renderStatusTag(status);
      },
    },
  ];

  const needsMappingCount = (rows as any[]).filter((r) => {
    const finalType = getProjectFinalCalyxType(r);
    return getStatusFromCalyxType(finalType) === 'needs-mapping';
  }).length;
  const mappedCount = (rows as any[]).length - needsMappingCount;

  return (
    <Spin spinning={loading} tip="Loading generic project ID mappings…">
      <div
        style={{
          margin: '16px 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search project ID, registry…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <Space wrap>
          <Tag color="green">Mapped: {mappedCount}</Tag>
          <Tag color="red">Needs Mapping: {needsMappingCount}</Tag>
          <Text type="secondary">Show:</Text>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 200 }}
            options={[
              { value: 'all', label: 'All projects' },
              { value: 'needs-mapping', label: 'Needs mapping' },
              { value: 'mapped', label: 'Mapped' },
            ]}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              queryClient.invalidateQueries(['genericProjectIdMappings']);
            }}
          >
            Refresh
          </Button>
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={filteredRows}
        rowKey="id"
        size="middle"
        pagination={{ defaultPageSize: 50, showSizeChanger: true }}
      />
      <ChangeLogsModal
        open={!!changeLogModalRecord}
        changeLogs={changeLogModalRecord?.change_logs || []}
        onClose={() => setChangeLogModalRecord(null)}
        onActionTakenAll={() => {
          const ids = (changeLogModalRecord?.change_logs || []).map((l: any) => l.id);
          if (ids.length > 0) markAllActionTaken(ids);
        }}
        actionLoading={actionLoading}
      />
    </Spin>
  );
}

// ─── Main Component with Tabs ──────────────────────────────────────────

function GenericMapper() {
  const { data: projectTypesData } = useQuery({
    queryKey: ['projectTypes'],
    queryFn: getProjectTypes,
  });

  const { data: ghgMethodologiesData } = useQuery({
    queryKey: ['ghgMethodologies'],
    queryFn: getGHGMethodologies,
  });

  const calyxOptions = useMemo(() => {
    const raw = Array.isArray(projectTypesData)
      ? projectTypesData
      : projectTypesData?.response || [];
    return (raw as any[]).map((pt) => ({
      value: pt.display_name,
      label: pt.display_name,
    }));
  }, [projectTypesData]);

  const calyxMethodologyOptions = useMemo(() => {
    const raw = Array.isArray(ghgMethodologiesData)
      ? ghgMethodologiesData
      : ghgMethodologiesData?.response || [];
    return (raw as any[]).map((m) => ({
      value: m.display_name,
      label: m.display_name,
    }));
  }, [ghgMethodologiesData]);

  return (
    <div
      style={{
        padding: 24,
        background: '#f5f5f5',
        minHeight: '100vh',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <Title level={3} style={{ marginBottom: 0 }}>
          Generic Project Type Mapper
        </Title>
        <Text type="secondary">
          Map harmonized methodologies and project IDs to Calyx project types.
        </Text>
      </div>

      <Alert
        type="info"
        showIcon
        closable
        message="Please note that any changes saved here will not be reflected on the platform immediately. The updates will be applied during the next registry sync, which runs automatically every Saturday."
        style={{ marginBottom: 16 }}
      />

      <Tabs
        defaultActiveKey="registry-type"
        type="card"
        items={[
          {
            key: 'registry-type',
            label: 'Methodology Level',
            children: (
              <RegistryTypeLevelTab
                calyxOptions={calyxOptions}
              />
            ),
          },
          {
            key: 'project-id',
            label: 'Project ID Override',
            children: (
              <ProjectIdLevelTab
                calyxOptions={calyxOptions}
                calyxMethodologyOptions={calyxMethodologyOptions}
              />
            ),
          },
        ]}
      />
    </div>
  );
}

export default GenericMapper;
